import { doc, runTransaction } from "firebase/firestore";
import { db } from "../../firebase";

// GIS-279. The operator's clock adjust (a minute on or off the live round) used
// to write an absolute `activeSession.timeLeft`: the number on screen plus or
// minus 60. Paid extends add their minutes on the server with an increment
// (GIS-199), and free extends do too, so an absolute write that landed a few
// seconds after one of them erased it, and a paid extend was charged with no
// minutes added.
//
// The adjust is a delta now. Inside a transaction we re-read timeLeft and apply
// what the operator meant (the value they asked for minus the value they saw)
// to whatever is stored, so an extend that landed in between survives.

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** The timeLeft to write, given what is stored now and what the operator saw and asked for. */
export const nextTimeLeft = ({ currentTimeLeft, seenTimeLeft, newTimeLeft }) => {
  // Nothing to apply a delta to (no clock stored yet, or the operator saw none):
  // use the value they asked for, which is what the old write did.
  if (!isFiniteNumber(currentTimeLeft) || !isFiniteNumber(seenTimeLeft)) return newTimeLeft;
  return currentTimeLeft + (newTimeLeft - seenTimeLeft);
};

/**
 * Apply an operator's clock adjust to events/{eventID} without clobbering a
 * concurrent extend. Resolves to `{ status, timeLeft }`:
 *   updated        the new timeLeft was written
 *   missing        the event doc does not exist
 *   round_changed  the round moved on since the operator looked; nothing written
 *   not_positive   the delta would leave no time on the clock; nothing written
 */
export async function adjustTimeLeftInTransaction(eventID, { seenTimeLeft, newTimeLeft, seenRound }) {
  const ref = doc(db, "events", eventID);
  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) return { status: "missing" };
    const session = snap.data()?.activeSession || {};
    // A minute off round 3 is not a minute off round 4. If the round advanced
    // under the operator, let them look again rather than guess.
    if (isFiniteNumber(seenRound) && isFiniteNumber(session.currentRound) && session.currentRound !== seenRound) {
      return { status: "round_changed", timeLeft: session.timeLeft };
    }
    const next = nextTimeLeft({ currentTimeLeft: session.timeLeft, seenTimeLeft, newTimeLeft });
    if (!(next > 0)) return { status: "not_positive", timeLeft: session.timeLeft };
    transaction.update(ref, { "activeSession.timeLeft": next });
    return { status: "updated", timeLeft: next };
  });
}
